/** @jsxImportSource @emotion/react */
import { useState, useRef, useEffect } from 'react'
import styled from '@emotion/styled'

export const TooltipContainer = styled.div({
  position: 'relative',
  display: 'inline-block',
})

export const TooltipContent = styled.div(({ theme }) => ({
  position: 'absolute',
  bottom: '95%',
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '0.5rem',
  backgroundColor: theme.brandColorOrange,
  color: 'white',
  borderRadius: '0.25rem',
  fontSize: '0.75rem',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  transition: 'opacity 0.2s',
  marginBottom: '0.5rem',
  zIndex: 50,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    borderWidth: '4px',
    borderStyle: 'solid',
    borderColor: ` ${theme.brandColorOrange} transparent transparent transparent `,
  },
}))

export const EditorContainer = styled.div(({ toolbarHidden, readOnly }) => ({
  border: toolbarHidden && readOnly ? 'none' : '1px solid #e2e8f0',
  borderRadius: toolbarHidden && readOnly ? 0 : '0.5rem',
  overflow: toolbarHidden ? (readOnly ? 'visible' : 'hidden') : 'visible',
  backgroundColor: 'transparent',
  display: toolbarHidden && readOnly ? 'flex' : 'block',
  flexDirection: 'column',
  flexGrow: 1,
}))

export const Toolbar = styled.div({
  padding: '0.5rem',
  borderBottom: '1px solid #e2e8f0',
  backgroundColor: '#f8fafc',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  alignItems: 'center',
})

export const ToolbarGroup = styled.div({
  display: 'flex',
  gap: '0.25rem',
  paddingRight: '0.5rem',
  marginRight: '0.5rem',
  borderRight: '1px solid #e2e8f0',
  position: 'relative',
  '&:last-child': {
    borderRight: 'none',
    marginRight: 0,
    paddingRight: 0,
  },
})

export const ToolbarButton = styled.button(({ active, theme }) => ({
  border: 'none',
  padding: '0.375rem',
  borderRadius: '0.25rem',
  backgroundColor: active ? '#e2e8f0' : 'transparent',
  color: active ? '#1e293b' : '#64748b',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: '#e2e8f0',
    color: '#1e293b',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}))

export const ColorButton = styled(ToolbarButton)((props) => ({
  minHeight: '36px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  padding: '0.375rem 0.5rem',
  '&::after': {
    content: '""',
    display: 'inline-block',
    width: '12px',
    height: '12px',
    backgroundColor: props.color || '#000000',
    border: '1px solid #e2e8f0',
    borderRadius: '2px',
  },
}))

export const ColorPickerContainer = styled.div({
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: '0.25rem',
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '0.375rem',
  boxShadow:
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  padding: '0.5rem',
  zIndex: 50,
  width: 'max-content',
})

export const ColorGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: '0.25rem',
})

export const ColorSwatch = styled.button((props) => ({
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: '0.25rem',
  border: '1px solid #e2e8f0',
  backgroundColor: props.color,
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    borderColor: '#94a3b8',
    transform: 'scale(1.1)',
  },
}))

export const Select = styled.select({
  padding: '0.125rem',
  borderRadius: '0.25rem',
  border: '1px solid #e2e8f0',
  backgroundColor: 'white',
  color: '#64748b',
  fontSize: '0.75rem',
  width: '82px',
  '&:focus': {
    outline: 'none',
    borderColor: '#94a3b8',
  },
})

export const ListMenu = styled.div((props) => ({
  display: props.isOpen ? 'flex' : 'none',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'nowrap',
  position: 'absolute',
  top: '100%',
  left: 0,
  background: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '0.375rem',
  boxShadow:
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  zIndex: 50,
  marginTop: '0.25rem',
}))

export const ListMenuItem = styled.button((props) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0,
  padding: '0.375rem',
  width: '100%',
  background: props.active ? '#f7fafc' : 'transparent',
  color: props.active ? '#2d3748' : '#4a5568',
  border: 'none',
  cursor: 'pointer',
  '&:hover': {
    background: '#f7fafc',
  },
  svg: {
    width: '1rem',
    height: '1rem',
  },
}))

export const StyledEditorContent = styled.div(
  ({ toolbarHidden, readOnly }) => ({
    padding: toolbarHidden && readOnly ? 0 : '1rem',
    minHeight: toolbarHidden && readOnly ? 'auto' : '100px',
    backgroundColor: toolbarHidden && readOnly ? 'transparent' : '#ffffff',
    flexGrow: 1,
    '.ProseMirror': {
      outline: 'none',
      minHeight: toolbarHidden && readOnly ? 'auto' : '150px',
      margin: 0,
      padding: 0,
      '> * + *': {
        marginTop: toolbarHidden && readOnly ? '0.25em' : '0.75em',
      },
      'ul, ol': {
        paddingLeft: toolbarHidden && readOnly ? '1rem' : '1.5rem',
        margin: toolbarHidden && readOnly ? '0.25rem 0' : '0.5rem 0',
      },
      'ul[data-type="disc"]': { listStyleType: 'disc' },
      'ul[data-type="circle"]': { listStyleType: 'circle' },
      'ul[data-type="square"]': { listStyleType: 'square' },
      'ol[data-type="decimal"]': { listStyleType: 'decimal' },
      'ol[data-type="lower-alpha"]': { listStyleType: 'lower-alpha' },
      'ol[data-type="upper-alpha"]': { listStyleType: 'upper-alpha' },
      'ol[data-type="lower-roman"]': { listStyleType: 'lower-roman' },
      'ol[data-type="upper-roman"]': { listStyleType: 'upper-roman' },
      h1: { fontSize: '2em', fontWeight: 'bold' },
      h2: { fontSize: '1.5em', fontWeight: 'bold' },
      h3: { fontSize: '1.17em', fontWeight: 'bold' },
      a: {
        color: '#2563eb',
        textDecoration: 'underline',
      },
      img: {
        maxWidth: '100%',
        height: 'auto',
      },
      blockquote: {
        borderLeft: '3px solid #e2e8f0',
        paddingLeft: '1rem',
        color: '#64748b',
      },
      code: {
        backgroundColor: '#f1f5f9',
        padding: '0.2em 0.4em',
        borderRadius: '0.25rem',
        fontFamily: 'monospace',
      },
      pre: {
        backgroundColor: '#1e293b',
        color: '#f8fafc',
        padding: '0.75rem',
        borderRadius: '0.5rem',
        overflowX: 'auto',
        code: {
          backgroundColor: 'transparent',
          padding: 0,
          color: 'inherit',
        },
      },
      '&[data-text-align="center"]': { textAlign: 'center' },
      '&[data-text-align="right"]': { textAlign: 'right' },
      '&[data-text-align="justify"]': { textAlign: 'justify' },
      'p.is-editor-empty::first-of-type::before': {
        content: 'attr(data-placeholder)',
        float: 'left',
        color: '#6b7280',
        pointerEvents: 'none',
        height: 0,
      },
    },
  }),
)

export const Tooltip = ({ content, children }) => {
  const [visible, setVisible] = useState(false)

  return (
    <TooltipContainer
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      <TooltipContent css={{ opacity: visible ? 1 : 0 }}>
        {content}
      </TooltipContent>
    </TooltipContainer>
  )
}
